import { gql, useQuery } from '@apollo/client';
import { Box, Paragraph } from '@zillow/constellation-10';
import { memo, useContext, useEffect, useRef, useState } from 'react';
import { inboxPageShouldScrollToRenterProfileVariable } from '../../../../lib/apollo';
import { useRenterProfileAttributesDataQuery } from '../../../../types/GraphqlTypes';
import { InboxPageContext } from '../../../../contexts/InboxPageContextProvider';
import { getFormattedAttributesList } from '../../../../utils/renterProfileUtils';
import InboxSidebarSection from '../InboxSidebarSection';

type RenterProfileAttributeProps = {
  title: string;
  content: string;
};

const RenterProfileAttributes = (): JSX.Element | null => {
  const { conversationId, listingAlias } = useContext(InboxPageContext);
  const renterProfileAttributesRef = useRef<HTMLDivElement>(null);
  const { data: renterProfileScrollData } = useQuery(GET_SHOULD_SCROLL_TO_RENTER_PROFILE, {
    fetchPolicy: 'cache-only',
  });
  const { data: renterProfileData } = useRenterProfileAttributesDataQuery({
    variables: {
      listingAlias: listingAlias!,
      conversationId: conversationId!,
    },
    fetchPolicy: 'cache-only',
  });
  const [isBackgroundHighlighted, setIsBackgroundHighlighted] = useState(false);

  useEffect(() => {
    if (renterProfileScrollData.inboxPageShouldScrollToRenterProfile && renterProfileAttributesRef.current) {
      renterProfileAttributesRef.current.scrollIntoView({
        behavior: 'smooth',
      });
      setIsBackgroundHighlighted(true);

      setTimeout(() => {
        setIsBackgroundHighlighted(false);
      }, 3000);

      inboxPageShouldScrollToRenterProfileVariable(false);
    }
  }, [renterProfileScrollData.inboxPageShouldScrollToRenterProfile]);

  if (!renterProfileData) {
    return null;
  }

  const { renterProfile } = renterProfileData.ZRM_getConversationById;
  if (!renterProfile || renterProfile.isRenterProfileHidden) {
    return null;
  }

  const attributes = getFormattedAttributesList(renterProfile.attributes);
  if (attributes.length === 0) return null;

  return (
    <InboxSidebarSection
      title="Renter Profile"
      ref={renterProfileAttributesRef}
      isBackgroundHighlighted={isBackgroundHighlighted}
    >
      <>
        {attributes.map((attribute) => (
          <RenterProfileAttribute key={attribute.title} title={attribute.title} content={attribute.value} />
        ))}
      </>
    </InboxSidebarSection>
  );
};

const RenterProfileAttribute = memo(({ title = '', content = '' }: RenterProfileAttributeProps) => {
  return (
    <Box>
      <Paragraph textStyle="body-sm-bold" css={{ color: 'text.subtle', marginTop: 'tight' }}>
        {title}
      </Paragraph>

      <Paragraph>{content}</Paragraph>
    </Box>
  );
});

RenterProfileAttribute.displayName = 'RenterProfileAttribute';
export default memo(RenterProfileAttributes);

const GET_SHOULD_SCROLL_TO_RENTER_PROFILE = gql`
  query {
    inboxPageShouldScrollToRenterProfile @client
  }
`;
